import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Mic, Square, ArrowLeft } from 'lucide-react';
import { toast } from '../ui/Toast';

const VoiceRecorder = ({ onResult, onBack }) => {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [action, setAction] = useState('transcribe');
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
        mediaRecorder.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunks.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        await processAudio(blob);
      };

      recorder.start();
      mediaRecorder.current = recorder;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch (err) {
      toast.error('Impossible d\'accéder au microphone. Vérifiez les permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
    }
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const processAudio = async (blob) => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const formData = new FormData();
      formData.append('file', blob, 'audio.webm');
      formData.append('language', 'fr');

      const transcribeRes = await axios.post('/api/voice/transcribe', formData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });

      const transcribedText = transcribeRes.data.text;

      if (action === 'transcribe') {
        onResult(transcribedText, 'Transcription');
      } else {
        const endpoint = action === 'summarize' ? '/api/voice/summarize' : '/api/voice/rewrite';
        const aiRes = await axios.post(endpoint, { text: transcribedText }, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          timeout: 60000,
        });
        const label = action === 'summarize' ? 'Résumé' : 'Réécriture';
        onResult(aiRes.data.markdown, label);
      }
    } catch (err) {
      toast.error('Erreur: ' + (err.response?.data?.details || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  if (processing) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #475569', borderTopColor: '#f97316', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <div style={{ color: '#94a3b8', fontSize: 14 }}>Traitement en cours...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} style={styles.backBtn}>
        <ArrowLeft size={14} /> Retour
      </button>

      {/* Action selector */}
      <div style={styles.actionRow}>
        {[['transcribe', 'Transcrire'], ['summarize', 'Résumer'], ['rewrite', 'Réécrire']].map(([key, label]) => (
          <button key={key} onClick={() => setAction(key)}
            style={{ ...styles.actionBtn, ...(action === key ? styles.actionActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {/* Record button */}
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <button onClick={recording ? stopRecording : startRecording}
          style={{ ...styles.recordBtn, ...(recording ? styles.recordBtnActive : {}) }}>
          {recording ? <Square size={24} /> : <Mic size={28} />}
        </button>
        <div style={{ color: recording ? '#f97316' : '#64748b', fontSize: 20, fontFamily: 'monospace', marginTop: 12 }}>
          {formatTime(seconds)}
        </div>
        <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
          {recording ? 'Cliquez pour arrêter' : 'Cliquez pour enregistrer'}
        </div>
      </div>
    </div>
  );
};

const styles = {
  backBtn: {
    background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, marginBottom: 16, padding: 0,
  },
  actionRow: {
    display: 'flex', gap: 6, marginBottom: 8,
  },
  actionBtn: {
    flex: 1, padding: '8px 0', borderRadius: 6, border: '1px solid #475569',
    background: '#334155', color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: 500,
  },
  actionActive: {
    background: '#f97316', color: 'white', borderColor: '#f97316',
  },
  recordBtn: {
    width: 72, height: 72, borderRadius: '50%', border: '3px solid #475569',
    background: '#334155', color: '#94a3b8', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s',
  },
  recordBtnActive: {
    borderColor: '#f97316', background: '#7f1d1d', color: '#fbbf24',
    animation: 'pulse-ring 1.5s infinite',
  },
};

export default VoiceRecorder;
